import { z } from 'zod';

import {
  EdgeRejectionReasonSchema,
  RelationIdSchema,
  SupplementalPublicationStatusSchema,
} from './edge.js';
import {
  HttpUrlSchema,
  NonEmptyStringSchema,
  QidSchema,
  Sha256Schema,
  StatementIdSchema,
  StatementRankSchema,
} from './primitives.js';
import { QualifiedTimeSchema } from './time.js';

export const CoreRelationshipPropertyIdSchema = z.enum([
  'P26',
  'P451',
  'P22',
  'P25',
  'P40',
  'P3373',
  'P1038',
  'P1290',
  'P3448',
  'P8810',
]);

export type CoreRelationshipPropertyId = z.infer<typeof CoreRelationshipPropertyIdSchema>;

export const CORE_RELATIONSHIP_PROPERTY_IDS = CoreRelationshipPropertyIdSchema.options;

const CoreRelationshipStatementCountsSchema = z.object({
  P26: z.number().int().nonnegative(),
  P451: z.number().int().nonnegative(),
  P22: z.number().int().nonnegative(),
  P25: z.number().int().nonnegative(),
  P40: z.number().int().nonnegative(),
  P3373: z.number().int().nonnegative(),
  P1038: z.number().int().nonnegative(),
  P1290: z.number().int().nonnegative(),
  P3448: z.number().int().nonnegative(),
  P8810: z.number().int().nonnegative(),
});

export const AcquisitionCoverageStatusSchema = z.enum(['returned', 'missing', 'errored']);

export const AcquisitionCoverageRecordSchema = z
  .object({
    kind: z.literal('acquisition'),
    entityId: QidSchema,
    status: AcquisitionCoverageStatusSchema,
    requestedProperties: z
      .array(CoreRelationshipPropertyIdSchema)
      .length(CORE_RELATIONSHIP_PROPERTY_IDS.length),
    requestDigests: z.array(Sha256Schema).min(1),
    sourceRevision: z.number().int().positive().optional(),
    propertyStatementCounts: CoreRelationshipStatementCountsSchema,
    errorCode: NonEmptyStringSchema.optional(),
  })
  .superRefine((record, context) => {
    if (
      record.requestedProperties.some(
        (propertyId, index) => propertyId !== CORE_RELATIONSHIP_PROPERTY_IDS[index],
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: ['requestedProperties'],
        message: 'Acquisition coverage properties must use the complete stable core order.',
      });
    }
    if (record.status === 'errored' && record.errorCode === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['errorCode'],
        message: 'Errored acquisitions require a stable error code.',
      });
    }
    if (record.status !== 'errored' && record.errorCode !== undefined) {
      context.addIssue({
        code: 'custom',
        path: ['errorCode'],
        message: 'Only errored acquisitions may carry an error code.',
      });
    }
    if (record.status !== 'returned' && record.sourceRevision !== undefined) {
      context.addIssue({
        code: 'custom',
        path: ['sourceRevision'],
        message: 'Only returned acquisitions may carry a source revision.',
      });
    }
    if (
      record.status !== 'returned' &&
      Object.values(record.propertyStatementCounts).some((count) => count !== 0)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['propertyStatementCounts'],
        message: 'Missing or errored acquisitions cannot report acquired statements.',
      });
    }
  });

export type AcquisitionCoverageRecord = z.infer<typeof AcquisitionCoverageRecordSchema>;

export const CoverageEndpointOutcomeSchema = z.enum([
  'selected-baseline',
  'selected-connector',
  'selected-context',
  'eligible-not-selected',
  'adult-not-selected',
  'minor-excluded',
  'unknown-age-excluded',
  'identity-ineligible',
  'not-acquired',
]);

export const CoverageTerminalOutcomeSchema = z.enum([
  'identity-conflict',
  'invalid-endpoint',
  'invalid-temporal-order',
  'relationship-role-conflict',
  'self-reference',
  'duplicate-merged',
  'selection-excluded',
  'policy-rejected',
  'accepted',
  'pipeline-defect',
]);

export const CoverageCandidateOutcomeSchema = z.enum([
  'not-created',
  'selection-excluded',
  'policy-rejected',
  'accepted',
  'pipeline-defect',
]);

export const CoverageCanonicalStatusSchema = z.enum([
  'not-accepted',
  'not-evaluated',
  'present',
  'suppressed',
  'missing',
]);

export const CoverageRuntimeStatusSchema = z.enum([
  'not-accepted',
  'not-evaluated',
  'preserved',
  'missing',
  'semantic-loss',
]);

const CoverageEvidenceSchema = z.object({
  retainedSourceIds: z.array(NonEmptyStringSchema),
  qualifyingSourceIds: z.array(NonEmptyStringSchema),
  hasNonQualifyingSources: z.boolean(),
  requirementSatisfied: z.boolean(),
  potentiallyLiving: z.boolean(),
});

export const StatementDecisionRecordSchema = z
  .object({
    kind: z.literal('statement'),
    statementId: StatementIdSchema,
    subjectId: QidSchema,
    objectId: QidSchema.optional(),
    propertyId: CoreRelationshipPropertyIdSchema,
    statementRank: StatementRankSchema,
    rawStart: z.array(QualifiedTimeSchema),
    rawEnd: z.array(QualifiedTimeSchema),
    rawRetainedReferenceCount: z.number().int().nonnegative(),
    sourceEndpointOutcome: CoverageEndpointOutcomeSchema,
    objectEndpointOutcome: CoverageEndpointOutcomeSchema.optional(),
    candidateId: NonEmptyStringSchema.optional(),
    survivingStatementId: StatementIdSchema.optional(),
    candidateStart: QualifiedTimeSchema.optional(),
    candidateEnd: QualifiedTimeSchema.optional(),
    evidence: CoverageEvidenceSchema.optional(),
    terminalOutcome: CoverageTerminalOutcomeSchema,
    candidateOutcome: CoverageCandidateOutcomeSchema,
    policyReasons: z.array(EdgeRejectionReasonSchema),
    canonicalStatus: CoverageCanonicalStatusSchema,
    runtimeStatus: CoverageRuntimeStatusSchema,
  })
  .superRefine((record, context) => {
    if (record.candidateOutcome === 'not-created') {
      if (record.candidateId !== undefined || record.survivingStatementId !== undefined) {
        context.addIssue({
          code: 'custom',
          path: ['candidateId'],
          message: 'A statement with no candidate cannot identify a candidate survivor.',
        });
      }
      if (
        ![
          'identity-conflict',
          'invalid-endpoint',
          'invalid-temporal-order',
          'relationship-role-conflict',
          'self-reference',
        ].includes(record.terminalOutcome)
      ) {
        context.addIssue({
          code: 'custom',
          path: ['terminalOutcome'],
          message: 'A statement with no candidate must terminate at extraction.',
        });
      }
    } else if (record.candidateId === undefined || record.survivingStatementId === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['candidateId'],
        message:
          'A created candidate must retain its candidate and surviving-statement identities.',
      });
    }
  });

export type StatementDecisionRecord = z.infer<typeof StatementDecisionRecordSchema>;

const WikipediaRelationshipParseSummarySchema = z.object({
  examinedTargetSections: z.number().int().nonnegative(),
  examinedNarrativeSections: z.number().int().nonnegative(),
  examinedRelationshipSentences: z.number().int().nonnegative(),
  examinedExplicitSubjectSentences: z.number().int().nonnegative(),
  extractedMentions: z.number().int().nonnegative(),
  skippedNegatedSentences: z.number().int().nonnegative(),
  skippedAmbiguousLinks: z.number().int().nonnegative(),
});

export const WikipediaRelationshipPageCoverageSchema = z
  .object({
    subjectId: QidSchema,
    requestedTitle: NonEmptyStringSchema,
    canonicalTitle: NonEmptyStringSchema,
    status: z.enum(['returned', 'missing', 'identity-mismatch']),
    requestDigest: Sha256Schema,
    resolvedSubjectId: QidSchema.optional(),
    revisionId: z.number().int().positive().optional(),
    revisionTimestamp: NonEmptyStringSchema.optional(),
    parse: WikipediaRelationshipParseSummarySchema,
  })
  .superRefine((record, context) => {
    if (
      record.status === 'returned' &&
      (record.revisionId === undefined || record.revisionTimestamp === undefined)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['revisionId'],
        message: 'Returned Wikipedia pages require an exact revision identity.',
      });
    }
    if (
      record.status === 'missing' &&
      (record.revisionId !== undefined ||
        record.revisionTimestamp !== undefined ||
        record.resolvedSubjectId !== undefined)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['revisionId'],
        message: 'Missing Wikipedia pages cannot carry a revision identity.',
      });
    }
    if (
      record.status === 'identity-mismatch' &&
      (record.revisionId === undefined ||
        record.revisionTimestamp === undefined ||
        record.resolvedSubjectId === record.subjectId)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['resolvedSubjectId'],
        message:
          'Identity-mismatched Wikipedia pages require an exact revision and cannot resolve back to the expected subject.',
      });
    }
  });

export type WikipediaRelationshipPageCoverage = z.infer<
  typeof WikipediaRelationshipPageCoverageSchema
>;

export const WikipediaRelationshipClaimOutcomeSchema = z.enum([
  'unresolved-title',
  'identity-mismatch',
  'non-human-target',
  'self-reference',
  'invalid-temporal-order',
  'selection-excluded',
  'policy-rejected',
  'accepted',
  'pipeline-defect',
]);

export const WikipediaRelationshipClaimDecisionSchema = z
  .object({
    mentionId: NonEmptyStringSchema,
    subjectId: QidSchema,
    targetTitle: NonEmptyStringSchema,
    targetId: QidSchema.optional(),
    sourcePageTitle: NonEmptyStringSchema,
    sourceRevision: z.number().int().positive(),
    section: NonEmptyStringSchema,
    propertyId: CoreRelationshipPropertyIdSchema,
    relation: RelationIdSchema,
    publicationStatusHint: SupplementalPublicationStatusSchema,
    candidateId: NonEmptyStringSchema.optional(),
    outcome: WikipediaRelationshipClaimOutcomeSchema,
    policyReasons: z.array(EdgeRejectionReasonSchema),
    canonicalStatus: CoverageCanonicalStatusSchema,
    runtimeStatus: CoverageRuntimeStatusSchema,
  })
  .superRefine((record, context) => {
    const hasCandidate = record.candidateId !== undefined;
    if (
      ['accepted', 'policy-rejected', 'selection-excluded', 'pipeline-defect'].includes(
        record.outcome,
      ) !== hasCandidate
    ) {
      context.addIssue({
        code: 'custom',
        path: ['candidateId'],
        message: 'Wikipedia claim candidate identity must match its terminal outcome.',
      });
    }
  });

export type WikipediaRelationshipClaimDecision = z.infer<
  typeof WikipediaRelationshipClaimDecisionSchema
>;

const MusicBrainzArtistIdSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u);

export const MusicBrainzRelationshipPageCoverageSchema = z
  .object({
    subjectId: QidSchema,
    artistId: MusicBrainzArtistIdSchema.optional(),
    status: z.enum([
      'no-identifier',
      'ambiguous-identifier',
      'returned',
      'missing',
      'non-person',
      'identity-mismatch',
    ]),
    requestDigest: Sha256Schema.optional(),
    resolvedSubjectId: QidSchema.optional(),
    eligibleRelationshipCount: z.number().int().nonnegative(),
  })
  .superRefine((record, context) => {
    const requested = !['no-identifier', 'ambiguous-identifier'].includes(record.status);
    if (requested !== (record.artistId !== undefined && record.requestDigest !== undefined)) {
      context.addIssue({
        code: 'custom',
        path: ['requestDigest'],
        message: 'Requested MusicBrainz pages require exact artist and request identities.',
      });
    }
    if (record.status !== 'returned' && record.eligibleRelationshipCount !== 0) {
      context.addIssue({
        code: 'custom',
        path: ['eligibleRelationshipCount'],
        message: 'Only returned MusicBrainz pages may report eligible relationships.',
      });
    }
    if (record.status !== 'identity-mismatch' && record.resolvedSubjectId !== undefined) {
      context.addIssue({
        code: 'custom',
        path: ['resolvedSubjectId'],
        message: 'Only identity-mismatched MusicBrainz pages may identify another subject.',
      });
    }
  });

export type MusicBrainzRelationshipPageCoverage = z.infer<
  typeof MusicBrainzRelationshipPageCoverageSchema
>;

export const MusicBrainzRelationshipClaimOutcomeSchema = z.enum([
  'unresolved-target',
  'ambiguous-target',
  'non-person-target',
  'identity-mismatch',
  'self-reference',
  'unsupported-attribute',
  'invalid-temporal-order',
  'selection-excluded',
  'policy-rejected',
  'accepted',
  'pipeline-defect',
]);

export const MusicBrainzRelationshipClaimDecisionSchema = z
  .object({
    claimId: NonEmptyStringSchema,
    subjectId: QidSchema,
    subjectArtistId: MusicBrainzArtistIdSchema,
    targetArtistId: MusicBrainzArtistIdSchema,
    targetName: NonEmptyStringSchema,
    targetId: QidSchema.optional(),
    typeId: z.string().uuid(),
    relationshipType: NonEmptyStringSchema,
    direction: z.enum(['forward', 'backward']),
    attributes: z.array(NonEmptyStringSchema),
    sourceUrl: HttpUrlSchema,
    begin: NonEmptyStringSchema.optional(),
    end: NonEmptyStringSchema.optional(),
    propertyId: CoreRelationshipPropertyIdSchema,
    relation: RelationIdSchema,
    publicationStatusHint: z.literal('reported'),
    candidateId: NonEmptyStringSchema.optional(),
    outcome: MusicBrainzRelationshipClaimOutcomeSchema,
    policyReasons: z.array(EdgeRejectionReasonSchema),
    canonicalStatus: CoverageCanonicalStatusSchema,
    runtimeStatus: CoverageRuntimeStatusSchema,
  })
  .superRefine((record, context) => {
    const hasCandidate = record.candidateId !== undefined;
    if (
      ['accepted', 'policy-rejected', 'selection-excluded', 'pipeline-defect'].includes(
        record.outcome,
      ) !== hasCandidate
    ) {
      context.addIssue({
        code: 'custom',
        path: ['candidateId'],
        message: 'MusicBrainz claim candidate identity must match its terminal outcome.',
      });
    }
  });

export type MusicBrainzRelationshipClaimDecision = z.infer<
  typeof MusicBrainzRelationshipClaimDecisionSchema
>;

export const CoverageDecisionLedgerSchema = z.object({
  schemaVersion: z.literal(4),
  reasonCodeVersion: z.literal(6),
  snapshotDate: NonEmptyStringSchema,
  configVersion: z.number().int().positive(),
  policyVersion: z.number().int().positive(),
  acquisitions: z.array(AcquisitionCoverageRecordSchema),
  statements: z.array(StatementDecisionRecordSchema),
  wikipedia: z.object({
    pages: z.array(WikipediaRelationshipPageCoverageSchema),
    claims: z.array(WikipediaRelationshipClaimDecisionSchema),
  }),
  musicBrainz: z.object({
    pages: z.array(MusicBrainzRelationshipPageCoverageSchema),
    claims: z.array(MusicBrainzRelationshipClaimDecisionSchema),
  }),
  views: z.object({
    topRecognitionEntityIds: z.array(QidSchema),
    modernEntertainmentEntityIds: z.array(QidSchema),
  }),
});

export type CoverageDecisionLedger = z.infer<typeof CoverageDecisionLedgerSchema>;
